import React, { useState } from 'react';
import { EyeOutlined, EyeInvisibleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { SPECIAL_CHARACTERS_REGEX } from '../../../../../utils/constants';
import './PasswordInput.scss';

interface PasswordInputProps {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  showRequirements?: boolean;
  requirementsValue?: string;
  wrapperClassName?: string;
  floatingLabel?: boolean;
  alwaysShowFloatingLabel?: boolean;
}

const PasswordInput: React.FC<PasswordInputProps> = ({ label, value, onChange, error, showRequirements = true, requirementsValue, wrapperClassName, floatingLabel = false, alwaysShowFloatingLabel = false }) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [hasSubmittedWithErrors, setHasSubmittedWithErrors] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  /**
   * Siempre validamos y calculamos estados (verde/rojo) en base al mismo valor.
   * Si se pasa `requirementsValue` (caso mobile: confirm password que muestra
   * los requisitos de la contraseña original), usamos ese valor; si no, usamos `value`.
   */
  const valueToValidate = typeof requirementsValue === 'string' ? requirementsValue : value;

  const passwordRequirements = [
    {
      label: "Lowercase letter",
      validator: (pwd: string) => /[a-z]/.test(pwd),
      met: false
    },
    {
      label: "Uppercase letter",
      validator: (pwd: string) => /[A-Z]/.test(pwd),
      met: false
    },
    {
      label: "Symbol",
      validator: (pwd: string) => SPECIAL_CHARACTERS_REGEX.test(pwd),
      met: false
    },
    {
      label: "8 characters",
      validator: (pwd: string) => pwd.length >= 8,
      met: false
    }
  ];

  const getPasswordRequirements = () => {
    return passwordRequirements.map(req => ({
      ...req,
      met: req.validator(valueToValidate || '')
    }));
  };

  const handlePasswordChange = (newValue: string): void => {
    if (onChange) {
      onChange(newValue);
    }
    if (hasSubmittedWithErrors) {
      setHasSubmittedWithErrors(false);
    }
  };

  const currentRequirements = getPasswordRequirements();
  // Para mobile usamos el mismo valor que se está validando (valueToValidate),
  // de forma que si solo se ha llenado la contraseña original, los requisitos
  // también puedan entrar en estado de error (rojo).
  const hasContent = typeof valueToValidate === 'number'
    ? true
    : Boolean(valueToValidate && valueToValidate.toString().trim().length > 0);
  // El floating label debe depender del valor PROPIO del campo, no de `valueToValidate`.
  // En Confirm Password se pasa requirementsValue=password, lo que hacía que el label se
  // mostrara aunque el campo de confirmación estuviera vacío, duplicándose con el placeholder.
  const hasOwnContent = typeof value === 'number'
    ? true
    : Boolean(value && value.toString().trim().length > 0);
  const showFloatingLabel = Boolean(
    floatingLabel &&
    label &&
    (alwaysShowFloatingLabel || isFocused || hasOwnContent)
  );

  return (
    <div className={`password-input-wrapper ${wrapperClassName ? wrapperClassName : ''}`}>
      <div className="password-input-container">
        <div className={`input-with-toggle${showFloatingLabel ? ' with-floating-label' : ''}`}>
          {showFloatingLabel && label && (
            <label className="floating-label">{label}</label>
          )}
          <input
            type={showPassword ? "text" : "password"}
            placeholder={label}
            value={value}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(event) => handlePasswordChange(event.target.value)}
            className={`password-input ${error ? 'error' : ''}`}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeInvisibleOutlined rev={undefined} /> : <EyeOutlined rev={undefined} />}
          </button>
        </div>
        {error && <p className='error-message'>{error}</p>}
      </div>

      {showRequirements && (
        <div className="password-requirements">
          <div className="requirements-grid">
            {currentRequirements.map((req, index) => (
              <div
                key={index}
                className={`requirement ${req.met ? 'met' : 'not-met'} ${!req.met && hasContent ? 'error-state' : ''} ${hasSubmittedWithErrors && !req.met ? 'shake-animation' : ''}`}
              >
                <span className="requirement-icon">
                  {req.met ? <CheckOutlined size={13} /> : <CloseOutlined size={13} />}
                </span>
                <span className="requirement-text">{req.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordInput; 